import { GridColDef } from "@material-ui/data-grid";
import { ReactComponent as RedTrashIcon } from "assets/icon/red_trash.svg";
import {
  Box,
  Button,
  Divider,
  Modal,
  Notification,
  Radio,
  Table,
  Typography,
} from "components";
import { MaterialIcon } from "components/common/MaterialIcon";
import { compose, withAuthorize, withHooks, withTranslation } from "enhancers";
import { TFunction } from "i18next";
import { map } from "lodash";
import { useEffect } from "react";
import styled from "styled-components";
import { AppColor } from "theme/app-color";
import { InsuranceSelectorTable } from "./InsuranceSelectorTable";

import { ReactComponent as CheckedRadioBoxIcon } from "assets/icon/checked_radio_box_icon.svg";
import { ReactComponent as UncheckRadioBoxIcon } from "assets/icon/uncheck_radio_box_icon.svg";
import Authorize from "components/Authorize";
import { PERMISSIONS } from "constants/enums/permissions";
import enrollmentStore from "stores/enrollmentStore";

const AddButton = styled(Button)`
  min-width: 151px;
  border-color: ${AppColor["Other/Info"]} !important;
  color: ${AppColor["Other/Info"]};
`;

interface InsuranceListProps {
  label: string;
  isAddModalOpen: boolean;
  masterInsurancePlansOptions: any[];
  handleClickAddInsurance: () => void;
  handleCloseAddModal: () => void;
  handleConfirmSelection: (values: any[]) => void;
  t: TFunction;
  tableData: [];
  columns: GridColDef[];
  viewOnly: boolean;
}

export const InsuranceListComponent = ({
  label,
  isAddModalOpen,
  masterInsurancePlansOptions,
  handleConfirmSelection,
  handleCloseAddModal,
  t,
  ...props
}: InsuranceListProps) => (
  <Box>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2">
        {t(".insuranceTypeHeader", { type: label })}
      </Typography>
      <Authorize permissions={[PERMISSIONS.ENROLLMENT_MANAGEMENT_EDIT]}>
        {!props.viewOnly && (
          <AddButton onClick={props.handleClickAddInsurance} variant="outlined">
            <MaterialIcon name="Add" mr={4} />
            {t(".add")}
          </AddButton>
        )}
      </Authorize>
    </Box>

    {props.tableData.length > 0 ? (
      <Box mt={4}>
        <Table
          className="EnrollmentInsuranceListTable"
          columns={props.columns}
          rows={props.tableData}
          density="compact"
          autoHeight
          rowsPerPageOptions={[5, 10]}
          style={{ minHeight: "40px" }}
          hideFooterPagination
          hideFooterRowCount
          disableSelectionOnClick
        />
      </Box>
    ) : (
      <Divider my={4} />
    )}
    <InsuranceSelectorTable
      isShow={isAddModalOpen}
      masterInsurancePlans={masterInsurancePlansOptions}
      onSelectionChange={handleConfirmSelection}
      onCancel={handleCloseAddModal}
    />
  </Box>
);

const enhancer = compose(
  withAuthorize(),
  withTranslation({
    prefix: "pages.main.enrollment.attendeeGrouping.InsuranceList",
  }),
  withHooks((props: any, hooks: any) => {
    const { useMemo, useCallback, useState } = hooks;
    const {
      label,
      id: insuranceTypeId,
      t,
      attendeeGroupId,
      viewOnly,
      masterBenefitInsurancePlans = [],
      masterInsurancePlans = [],
      hasPermission,
    } = props;

    const [selectedInsurances, setSelectedInsurances] = useState(
      masterBenefitInsurancePlans
    );

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const hasEditPermission = useMemo(
      () => hasPermission([PERMISSIONS.ENROLLMENT_MANAGEMENT_EDIT]),
      [hasPermission]
    );

    const adjustIsDefaultBeforeSet = useCallback((selected: any) => {
      const hasAtLeastOneDefault = selected.find((plan: any) => plan.isDefault);
      if (!hasAtLeastOneDefault) {
        const adjusted = selected.map((plan: any, ind: number) => ({
          ...plan,
          isDefault: ind === 0,
        }));
        setSelectedInsurances(adjusted);
      } else {
        setSelectedInsurances(selected);
      }
    }, []);

    const handleConfirmDelete = useCallback(
      (id: string) => {
        const filtered = selectedInsurances.filter(
          (insurance: any) => insurance.id !== id
        );

        adjustIsDefaultBeforeSet(filtered);
        enrollmentStore.removeInsuranceAttendeeGroupingTemp(
          attendeeGroupId,
          id
        );
        Notification.notify(t(".deleteSuccess"));
      },
      [t, selectedInsurances, adjustIsDefaultBeforeSet, attendeeGroupId]
    );

    const handleClickDeleteInsurance = useCallback(
      (props: any) => {
        const name = props.row.name;
        Modal.open({
          title: t(".deleteInsuranceModalTitle"),
          children: (
            <>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <Typography variant="body1" color={AppColor["Text/Dark Grey"]}>
                  {t(".deleteInsuranceModalInfo1")} &nbsp;
                </Typography>
                <Typography variant="body2" color={AppColor["Text/Black"]}>
                  {name} &nbsp;
                </Typography>
                <Typography variant="body1" color={AppColor["Text/Dark Grey"]}>
                  {t(".deleteModalInfo2")}
                </Typography>
              </div>
            </>
          ),
          cancelButtonLabel: t(".close"),
          okButtonLabel: t(".confirmDelete"),
          onOk: ({ close }: any) => {
            handleConfirmDelete(props.id);
            close();
          },
        });
      },
      [t, handleConfirmDelete]
    );

    const handleSetDefault = useCallback(
      (id: any) => {
        const result = selectedInsurances.map((insurance: any) => {
          return { ...insurance, isDefault: insurance.id === id };
        });
        setSelectedInsurances(result);
      },
      [selectedInsurances]
    );

    const tableData = useMemo(() => {
      return (
        selectedInsurances.map((data: any) => ({
          id: data.id,
          isDefault: data.isDefault,
          name: data.masterInsurancePlan.name,
          remark: data.masterInsurancePlan.remark,
          actions:
            !hasEditPermission || viewOnly
              ? []
              : [
                  {
                    Icon: RedTrashIcon,
                    onClick: handleClickDeleteInsurance,
                  },
                ],
        })) || []
      );
    }, [
      handleClickDeleteInsurance,
      viewOnly,
      hasEditPermission,
      selectedInsurances,
    ]);

    const columns: GridColDef[] = useMemo(
      (): GridColDef[] => [
        {
          width: 250,
          field: "name",
          headerName: t(".insuranceName") || "",
        },
        {
          width: 250,
          field: "remark",
          headerName: t(".remark") || "",
        },
        {
          width: 90,
          field: "isDefault",
          headerName: t(".default") || "",
          filterable: false,
          sortable: false,
          renderCell: (params) => (
            <Radio
              checked={params.row.isDefault}
              icon={<UncheckRadioBoxIcon />}
              checkedIcon={<CheckedRadioBoxIcon />}
              disabled={!hasEditPermission || viewOnly}
              onClick={() => handleSetDefault(params.id)}
            />
          ),
        },
        {
          width: 70,
          field: "actions",
          headerName: t(".delete"),
          filterable: false,
          sortable: false,
          type: "actions",
        },
      ],
      [t, handleSetDefault, hasEditPermission, viewOnly]
    );

    const handleConfirmSelection = useCallback(
      (values: any) => {
        adjustIsDefaultBeforeSet([...selectedInsurances, ...values]);
        setIsAddModalOpen(false);
      },
      [selectedInsurances, adjustIsDefaultBeforeSet]
    );

    const handleCloseAddModal = useCallback(() => {
      setIsAddModalOpen(false);
    }, []);

    const handleClickAddInsurance = useCallback(async () => {
      setIsAddModalOpen(true);
    }, []);

    const currentTypeInsurance = useMemo(() => {
      const displayMasterBenefitInsurancePlans = masterInsurancePlans.filter(
        (plan: any) => plan.insuranceType.id === insuranceTypeId
      );
      return displayMasterBenefitInsurancePlans;
    }, [masterInsurancePlans, insuranceTypeId]);

    const masterInsurancePlansOptions = useMemo(() => {
      const selectedIds = map(selectedInsurances, "masterInsurancePlan.id");
      return currentTypeInsurance.filter(
        (plan: any) => !selectedIds.includes(plan.id)
      );
    }, [currentTypeInsurance, selectedInsurances]);

    useEffect(() => {
      enrollmentStore.saveInsuranceAttendeeGroupingTemp(
        attendeeGroupId,
        selectedInsurances
      );
    }, [selectedInsurances, attendeeGroupId]);

    return {
      label,
      handleClickAddInsurance,
      tableData,
      columns,
      viewOnly,
      isAddModalOpen,
      masterInsurancePlansOptions,
      handleConfirmSelection,
      handleCloseAddModal,
    };
  })
);

export const InsuranceList = enhancer(InsuranceListComponent);
